import React, { useRef, useEffect } from 'react';
import styles from './WizardLayout.module.css';

const STEPS = [
  { id: 1, label: 'פרטים אישיים' },
  { id: 2, label: 'פרטי הפרויקט' },
  { id: 3, label: 'קומות' },
  { id: 4, label: 'תוכניות' },
  { id: 5, label: 'חללים' },
  { id: 6, label: 'העדפות' },
  { id: 7, label: 'סיום' },
];

const WizardLayout = ({
  currentStep,
  children,
  onBack,
  onContinue,
  onStepClick,
  isBackDisabled = false,
  isContinueDisabled = false,
  continueLabel = 'המשך',
  isLoading = false,
  error = null,
  userName = null,
  projectAddress = null,
}) => {
  // Scroll content to top when step changes
  const contentRef = useRef(null);
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Calculate progress percentage for the bottom progress bar
  const progressPercent = (currentStep / STEPS.length) * 100;

  // Helper to get step classes
  const getStepClasses = stepId => {
    const classes = [styles.progressStep];
    if (currentStep > stepId) classes.push(styles.completed);
    if (currentStep >= stepId) classes.push(styles.active);
    if (currentStep === stepId) classes.push(styles.current);
    return classes.join(' ');
  };

  // Get current step label for mobile
  const currentStepLabel = STEPS.find(s => s.id === currentStep)?.label || '';

  return (
    <div className={styles.wizardContainer} dir="rtl">
      {/* User/Project Header */}
      {(userName || projectAddress) && (
        <div className={styles.userHeader}>
          <div className={styles.userHeaderInner}>
            {userName && <span className={styles.userName}>שלום, {userName}</span>}
            {userName && projectAddress && <span className={styles.headerDivider}>|</span>}
            {projectAddress && <span className={styles.projectAddress}>{projectAddress}</span>}
          </div>
        </div>
      )}

      {/* Desktop Progress Bar */}
      <div className={styles.progressContainer}>
        <div className={styles.progressBar}>
          {STEPS.map((step, index) => (
            <div key={step.id} className={getStepClasses(step.id)}>
              <div
                className={`${styles.stepIndicator} ${onStepClick && currentStep > step.id ? styles.clickable : ''}`}
                onClick={() => onStepClick && currentStep > step.id && onStepClick(step.id)}
                role={onStepClick && currentStep > step.id ? 'button' : undefined}
                tabIndex={onStepClick && currentStep > step.id ? 0 : undefined}
              >
                {currentStep > step.id ? (
                  <span className={styles.checkmark}>✓</span>
                ) : (
                  <span className={styles.stepNumber}>{step.id}</span>
                )}
              </div>
              <span className={styles.stepLabel}>{step.label}</span>
              {index < STEPS.length - 1 && <div className={styles.stepConnector} />}
            </div>
          ))}
        </div>

        {/* Mobile Progress Bar */}
        <div className={styles.mobileProgress}>
          <div className={styles.mobileProgressHeader}>
            <span className={styles.mobileStepLabel}>{currentStepLabel}</span>
            <span className={styles.mobileStepCount}>
              {currentStep} / {STEPS.length}
            </span>
          </div>
          <div className={styles.mobileProgressTrack}>
            <div className={styles.mobileProgressFill} style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className={styles.contentContainer} ref={contentRef}>
        <div className={styles.contentInner}>
          {error && <div className={styles.errorMessage}>{error}</div>}
          {children}
        </div>
      </div>

      {/* Footer with Navigation Buttons */}
      <div className={styles.footer} style={{ '--progress-width': `${progressPercent}%` }}>
        <div className={styles.footerInner}>
          <button
            className={styles.backButton}
            onClick={onBack}
            disabled={isBackDisabled || isLoading}
          >
            חזור →
          </button>
          <button
            className={styles.continueButton}
            onClick={onContinue}
            disabled={isContinueDisabled || isLoading}
          >
            ← {isLoading ? 'שומר...' : continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WizardLayout;
